import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast, type Id } from 'react-toastify';

// An error raised just before a redirect (e.g. a guard that sends you elsewhere)
// is still about where you're going, so give it a moment before it counts as stale.
const REDIRECT_GRACE_MS = 1500;

/**
 * QA PR-12: an error or warning toast from one page no longer follows the user to
 * the next. Success and info toasts are left alone, because they often announce the
 * result of the action that caused the navigation ("1 request sent successfully").
 * A toast raised with `data: { keepAcrossPages: true }` (authorization holds) stays.
 */
export function useDismissStaleToastsOnNavigate(): void {
  const { pathname } = useLocation();
  const currentPath = useRef(pathname);
  const openProblems = useRef(new Map<Id, { path: string; at: number }>());

  useEffect(() => toast.onChange(item => {
    const keep = (item.data as { keepAcrossPages?: boolean } | undefined)?.keepAcrossPages === true;
    if (item.status === 'added' && !keep && (item.type === 'error' || item.type === 'warning')) {
      openProblems.current.set(item.id, { path: currentPath.current, at: Date.now() });
    } else if (item.status === 'removed') {
      openProblems.current.delete(item.id);
    }
  }), []);

  useEffect(() => {
    if (currentPath.current === pathname) return;
    currentPath.current = pathname;
    const now = Date.now();
    for (const [id, raised] of openProblems.current) {
      if (raised.path !== pathname && now - raised.at > REDIRECT_GRACE_MS) toast.dismiss(id);
    }
  }, [pathname]);
}
